import { RequireAtLeastOne } from 'type-fest';

export namespace Messenger {

    export namespace Message {

        /**
         * @todo
         *    Move here all types from src/channels/adapters/messenger.ts and src/channels/adapters/facebook/webhookTypes.ts
         *    that are related to message contents
         */
        export interface Special {
            tag?: Tag;
            feedComment?: Feed.Comment;
            feedReaction?: Feed.Reaction;
            /**
             * Send message as private reply to a comment or post
             */
            replyTo?: RequireAtLeastOne<{
                comment_id: string;
                post_id: string;
            }, 'comment_id' | 'post_id'>;
            /**
             * "title" properties of all attachments in the message that are of type "fallback"
             */
            fallbackAttachments?: string[];
            quickReplies?: QuickReply.QuickReply[];
            template?: Template.Template;
        }

        /**
         * Tag values to attach to messenger message to send outside of 24h window
         * @see https://developers.facebook.com/docs/messenger-platform/reference/send-api/#message-tag-usages
         */
        export enum Tag {
            /**
             * Tags the message you are sending to your customer as a non-recurring update to their application or account.
             * Not available for Instagram Messaging API
             */
            ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
            /**
             * Tags the message you are sending to your customer as a reminder fo an upcoming event or an update for an event in procgres for which the customer is registered.
             * Not available for Instagram Messaging API
             */
            CONFIRMED_EVENT_UPDATE = 'CONFIRMED_EVENT_UPDATE',
            /**
             * Tags the message you are sending to your customer as a Customer Feedback Survey. Customer feedback messages must be sent within 7 days of the customer's last message.
             * Not available for Instagram Messaging API
             */
            CUSTOMER_FEEDBACK = 'CUSTOMER_FEEDBACK',
            /**
             * When this tag is added to a message to a customer, it allows a human agent to respond to a person's message. Messages can be sent within 7 days of the person's.
             * Human agent support is for issues that cannot be resolved within the standard 24 hour messaging window
             */
            HUMAN_AGENT = 'HUMAN_AGENT',
            /**
             * Tags the message you are sending to your customer as an update for a recent purchase made by the customer. Not available for Instagram Messaging API.
             */
            POST_PURCHASE_UPDATE = 'POST_PURCHASE_UPDATE',
        }

        export namespace QuickReply {

            export type QuickReply = Text | Phone | Email;

            export type Text = {
                content_type: Type.text;
                title: string;
                payload: string;
                image_url?: string;
            };

            export type Phone = {
                content_type: Type.user_phone_number;
            };

            export type Email = {
                content_type: Type.user_email;
            };

            export enum Type {
                text = 'text',
                /** Sends a button allowing recipient to send the phone number associated with their account. */
                user_phone_number = 'user_phone_number',
                /** Sends a button allowing recipient to send the email associated with their account. */
                user_email = 'user_email',
            }
        }

        export namespace Template {

            export type Template = Media | Button | Generic | Receipt | Product | Feedback;

            export type Media = {
                template_type: 'media';
                /**
                 * Enable the native share button in Messenger for the template message.
                 * (Seems it not really works at time this code written)
                 */
                sharable?: boolean;
                elements: [
                    {
                        media_type: 'image' | 'video';
                        /** 1 - 3 buttons is supported */
                        buttons?: TemplateButton[];
                    } & (
                        {
                            /** The attachment ID of the image or video. */
                            attachment_id: string;
                        } | {
                            /** The URL of the image. */
                            url: string;
                        }
                    )
                ];
            };

            export type Button = {
                template_type: 'button';
                /**
                 * UTF-8-encoded text of up to 640 characters. Text will appear above the buttons.
                 */
                text: string;
                /** 1 - 3 buttons is supported */
                buttons: TemplateButton[];
            };

            export type Generic = {
                template_type: 'generic';
                /**
                 * Enable the native share button in Messenger for the template message.
                 * (Seems it not really works at time this code written)
                 */
                sharable?: boolean;
                /** The generic template supports a maximum of 10 elements per message. */
                elements: GenericElement[];
            };

            /** At least one property must be set in addition to title. */
            export type GenericElement = {
                /** The title to display in the template. 80 character limit. */
                title: string;
                /** The subtitle to display in the template. 80 character limit. */
                subtitle?: string;
                /** The URL of the image to display in the template. */
                image_url?: string;
                /** The default action executed when the template is tapped. */
                default_action?: Omit<UrlButton, 'title'>;
                /** An array of buttons to append to the template. A maximum of 3 buttons per element is supported. */
                buttons?: TemplateButton[];
            };

            /**
             * @todo Define fully if would be needed
             */
            export type Receipt = {
                template_type: 'receipt';
                [x: string]: any;
            };

            /**
             * @todo Define fully if would be needed
             */
            export type Product = {
                template_type: 'product';
                [x: string]: any;
            };

            /**
             * @todo Define fully if would be needed
             */
            export type Feedback = {
                template_type: 'customer_feedback';
                [x: string]: any;
            };

            export type TemplateButton = LogInButton | LogOutButton | CallButton | PostbackButton | UrlButton;

            /**
             * The log in button is used in the account linking flow,
             * which lets you link the message recipient's identity on Messenger
             * with their account on your site by directing them to your web-based login flow for authentication.
             */
            export type LogInButton = {
                type: 'account_link';
                /** Authentication callback URL. Must use HTTPS protocol. */
                url: string;
            };

            export type LogOutButton = {
                type: 'account_unlink';
            };

            export type CallButton = {
                type: 'phone_number';
                /** Button title. 20 character limit. */
                title: string;
                /**
                 * Phone number to call. Must be in full E.164 format with "+" prefix
                 */
                payload: string;
            };

            export type PostbackButton = {
                type: 'postback';
                /** Button title. 20 character limit. */
                title: string;
                /**
                 * This data will be sent back to webhook. 1000 character limit.
                 */
                payload: string;
            };

            export type UrlButton = {
                type: 'web_url';
                /** Button title. 20 character limit. */
                title: string;
                /**
                 * This URL is opened in a mobile browser when the button is tapped. Must use HTTPS protocol if messenger_extensions is true.
                 */
                url: string;
                /** Height of the Webview. Defaults to "full" */
                webview_height_ratio?: 'compact' | 'tall' | 'full';
                /** Must be true if using Messenger Extensions. */
                messenger_extensions?: boolean;
                /**
                 * The URL to use on clients that don't support Messenger Extensions.
                 * If this is not defined, the url will be used as the fallback.
                 * It may only be specified if messenger_extensions is true.
                 */
                fallback_url?: string;
                /**
                 * Set to hide to disable the share button in the Webview (for sensitive info).
                 * This does not affect any shares initiated by the developer using Extensions.
                 */
                webview_share_button?: 'hide';
            };
        }
    }

    export namespace Feed {

        export type Reaction = {
            type: ReactionType;
            isEdited?: boolean;
            /**
             * Timestamp of the reaction creation in milliseconds
             */
            createdTime: number;
            post: Post;
        };

        export enum ReactionType {
            like = 'like',
            love = 'love',
            care = 'care',
            haha = 'haha',
            wow = 'wow',
            sad = 'sad',
            angry = 'angry',
        }

        export type Comment = RequireAtLeastOne<
            {
                id: string;
                message: string;
                photo: string;
                video: string;
                isEdited?: boolean;
                /**
                 * Timestamp of the comment creation in milliseconds
                 */
                createdTime: number;
                post: Post;
            },
            'message' | 'photo' | 'video'
        >;

        export type Post = {
            id: string;
            is_published: boolean;
            permalink_url: string;
            updated_time: string;
            status_type: string;
        };
    }
}
